import { useEffect, useState, useCallback, useRef } from "react";
import { deleteWordDocument, downloadWordDocument, addTemplateFolder, deleteTemplateFolder, updateTemplateFolderName, updateWordDocumentFolder, fetchTemplateFolderById, WordDocumentSearchResult, searchWordDocuments, fetchFolderTree, updateWordDocumentName } from "../api/wordDocuments";
import { Button } from "./ui/button";
import { Plus, Trash2, Download, CheckCircle2, X, Info, MinusCircle, FileText, Folder, MoreVertical, FolderPlus, Clock } from "lucide-react";
import { UploadModal } from "../components/UploadModal";
import "../styles/FileBoard.css";
import { ReactComponent as WordIcon } from '../word.svg';
import { useAuth } from "../context/AuthContext";
import { Loader } from "./ui/loader";
import Toast from "./fileboard/Toast";
import ConfirmModal from "./fileboard/ConfirmModal";
import ReactDOM from "react-dom";
import { FormulaBuilderModal } from "./ui/FormulaBuilderModal";
import { useNavigate } from "react-router-dom";
import WordPreview from "./WordPreview";

// Types
interface Document {
  id: string;
  fileName: string;
  createdAt: string;
  automationName?: string;
  automationId?: string;
  folderId?: string;
}
// Rozszerzony typ folderu z backendu (children, documents)
type TemplateFolderWithContent = {
  id: string;
  name: string;
  createdAt?: string;
  parentId?: string | null;
  children?: any[];
  documents?: any[];
};

interface FileBoardProps {
  setActiveTab: any;
  setAutomationSearch: any;
  onJumpToAutomation?: (automationName: string, folderId: string | null, automationId?: string) => void;
}

export default function FileBoard({ setActiveTab, setAutomationSearch, onJumpToAutomation }: FileBoardProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"fileName" | "createdAt">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAgendaOpen, setIsAgendaOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const tenantId = user?.tenantId;
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<any[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string | null, name: string }[]>([{ id: null, name: 'Wszystkie Szablony' }]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isDeletingFolder, setIsDeletingFolder] = useState<string | null>(null);
  const [isEditingFolder, setIsEditingFolder] = useState<any>(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [draggedDocId, setDraggedDocId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [dragOverBreadcrumbId, setDragOverBreadcrumbId] = useState<string | null>(null);
  const [isEditingDoc, setIsEditingDoc] = useState<any>(null);
  const [editDocName, setEditDocName] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLButtonElement | null>(null);
  const [searchSuggestions, setSearchSuggestions] = useState<WordDocumentSearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<WordDocumentSearchResult[]>([]);
  const [isFullSearch, setIsFullSearch] = useState(false);
  const suppressSuggestionsRef = useRef(false);
  const [folderTree, setFolderTree] = useState<any[] | null>(null);
  const [isFormulaModalOpen, setIsFormulaModalOpen] = useState(false);
  // Przykładowe zmienne do kreatora (TODO: podmień na dynamiczne z szablonu jeśli dostępne)
  const formulaVariables = ["IMIE I NAZWISKO", "EMAIL", "DATA", "KWOTA"];
  const navigate = useNavigate();
  const [previewDoc, setPreviewDoc] = useState(null);

  // Ładowanie całego drzewa na start
  useEffect(() => {
    if (!tenantId) return;
    fetchFolderTree(tenantId).then(tree => {
      setFolderTree(tree);
    });
  }, [tenantId]);

  // Dodaję nowy efekt, który ładuje foldery i dokumenty po zmianie folderTree lub innych zależności
  useEffect(() => {
    if (folderTree) {
      loadFoldersAndDocs();
    }
  }, [folderTree, tenantId, currentFolderId, sortBy, sortOrder]);

  const loadFoldersAndDocs = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      let folders: any[] = [];
      let documents: any[] = [];
      if (currentFolderId === null && folderTree) {
        // root: foldery najwyższego poziomu
        folders = folderTree.filter(f => f.parentId === null && f.id !== null && f.name !== null);
        // pliki root są w obiekcie z id: null, name: null
        const rootObj = folderTree.find(f => f.id === null && f.name === null);
        documents = rootObj?.documents || [];
      } else if (currentFolderId) {
        // pobierz folder z backendu (nowy format: children, documents)
        const folderData: TemplateFolderWithContent = await fetchTemplateFolderById(tenantId, currentFolderId);
        folders = folderData.children || [];
        documents = folderData.documents || [];
      }
      setFolders(folders);
      setDocuments(documents);
    } catch (e) {
      setToast({ message: 'Błąd ładowania folderów lub szablonów.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentFolderId, folderTree]);

  const handleDelete = async (documentId: string) => {
    try {
      await deleteWordDocument(tenantId, documentId);
      setDocuments(documents.filter((doc) => doc.id !== documentId));
      setToast({ message: "Plik został usunięty!", type: "success" });
      if (isFullSearch) {
        await refreshTreeAndContext();
        setSearchQuery("");
        setIsFullSearch(false);
        setSearchResults([]);
      }
    } catch (error) {
      setToast({ message: "Błąd usuwania pliku.", type: "error" });
    }
  };

  const handleDownload = async (documentId: string) => {
    try {
      await downloadWordDocument(tenantId, documentId);
      setToast({ message: "Plik został pobrany!", type: "success" });
    } catch (error) {
      setToast({ message: "Błąd pobierania pliku.", type: "error" });
    }
  };

  const handleAddFolder = async (name: string) => {
    if (!tenantId) return;
    try {
      await addTemplateFolder(tenantId, name, currentFolderId || undefined);
      await refreshTreeAndContext();
    } catch (e) {
      let msg = 'Błąd dodawania folderu.';
      if (e instanceof Response) {
        try {
          const data = await e.json();
          if (data && data.error) msg = data.error;
        } catch {}
      } else if (e && e.message) {
        msg = e.message;
      }
      setToast({ message: msg, type: 'error' });
      throw e;
    }
  };

  const handleDeleteFolder = async (id: string) => {
    if (!tenantId) return;
    try {
      await deleteTemplateFolder(tenantId, id);
      setToast({ message: 'Folder został usunięty!', type: 'success' });
      await refreshTreeAndContext();
    } catch (e) {
      setToast({ message: 'Błąd usuwania folderu.', type: 'error' });
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    }
    if (openDropdownId) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  // Search suggestions logic
  useEffect(() => {
    if (!tenantId || !searchQuery.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setIsFullSearch(false);
      setSearchResults([]);
      // Jeśli searchQuery jest puste, odśwież listę
      if (searchQuery === "") {
        loadFoldersAndDocs();
      }
      return;
    }
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false;
      return;
    }
    const timeout = setTimeout(async () => {
      const results = await searchWordDocuments(tenantId, searchQuery.trim());
      setSearchSuggestions(results.slice(0, 5));
      setShowSuggestions(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, [searchQuery, tenantId]);

  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [searchSuggestions, searchQuery, showSuggestions]);

  function handleSearchInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showSuggestions && searchSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(idx => Math.min(idx + 1, searchSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(idx => Math.max(idx - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < searchSuggestions.length) {
          handleSuggestionClick(searchSuggestions[selectedSuggestionIndex]);
          return;
        }
        setShowSuggestions(false);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
  }

  function handleSuggestionClick(doc: WordDocumentSearchResult) {
    suppressSuggestionsRef.current = true;
    setShowSuggestions(false);
    setSearchQuery(doc.fileName);
    if (doc.folderId) {
      setIsFullSearch(true);
      setSearchResults([doc]);
      setCurrentFolderId(doc.folderId);
      if (folderTree) {
        const path = findFolderPath(folderTree, doc.folderId);
        let crumbs = [{ id: null, name: 'Wszystkie Szablony' }, ...path.map(f => ({ id: f.id, name: f.name }))];
        if (crumbs.length > 1 && crumbs[0].name === crumbs[1].name) {
          crumbs = crumbs.slice(1);
        }
        setBreadcrumbs(crumbs);
      }
      setTimeout(() => {
        const el = document.getElementById(`doc-row-${doc.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } else {
      setIsFullSearch(true);
      setSearchResults([doc]);
    }
  }

  async function handleShowAllResults() {
    if (!tenantId || !searchQuery.trim()) return;
    setIsFullSearch(true);
    setShowSuggestions(false);
    const results = await searchWordDocuments(tenantId, searchQuery.trim());
    setSearchResults(results);
  }

  // Helper: recursively find the path from root to a folder by id in the folderTree
  function findFolderPath(tree, targetId) {
    for (const node of tree) {
      if (node.id === targetId) return [node];
      if (node.children && node.children.length > 0) {
        const childPath = findFolderPath(node.children, targetId);
        if (childPath.length > 0) return [node, ...childPath];
      }
    }
    return [];
  }

  // Helper: count all automated templates in a folder and its subfolders
  function countAutomatedTemplates(folder, folderTree) {
    if (!folder) return 0;
    let count = 0;
    if (folder.documents) {
      count += folder.documents.filter(doc => doc.automationName).length;
    }
    if (folder.children && folder.children.length > 0) {
      for (const child of folder.children) {
        // Find the full child node in the tree if possible
        let fullChild = null;
        if (folderTree) {
          // Try to find the child in the current children array
          fullChild = (folder.children || []).find(f => f.id === child.id) || child;
        }
        count += countAutomatedTemplates(fullChild, folderTree);
      }
    }
    return count;
  }

  // Update breadcrumbs when currentFolderId changes
  useEffect(() => {
    if (!tenantId) return;
    if (currentFolderId && folderTree) {
      // Recursively find the path to the folder in the tree
      const path = findFolderPath(folderTree, currentFolderId);
      if (path.length > 0) {
        // Usuń duplikaty root jeśli występują
        const crumbs = [{ id: null, name: 'Wszystkie Szablony' }, ...path.map(f => ({ id: f.id, name: f.name }))];
        if (crumbs.length > 1 && crumbs[0].name === crumbs[1].name) {
          setBreadcrumbs(crumbs.slice(1));
        } else {
          setBreadcrumbs(crumbs);
        }
      } else {
        setBreadcrumbs([{ id: null, name: 'Wszystkie Szablony' }]);
      }
    } else {
      // Always reload context when going to root
      loadFoldersAndDocs();
    }
  }, [tenantId, currentFolderId, folderTree]);

  // Helper to refresh folder tree and context
  async function refreshTreeAndContext() {
    if (!tenantId) return;
    const tree = await fetchFolderTree(tenantId);
    setFolderTree(tree);
    // loadFoldersAndDocs will be triggered by useEffect on folderTree change
  }

  function handleEnterFolderOrBreadcrumb(folderId: string | null) {
    setCurrentFolderId(folderId);
    setSearchQuery("");
    setIsFullSearch(false);
    setSearchResults([]);
    // If going to root, refresh tree and context
    if (folderId === null) {
      setBreadcrumbs([{ id: null, name: 'Wszystkie Szablony' }]);
      refreshTreeAndContext();
    }
  }

  // Dodaję funkcję pomocniczą do sprawdzania, czy folder (i podfoldery) zawiera zautomatyzowany szablon
  function folderHasAutomatedTemplate(folder, folderTree) {
    if (!folder) return false;
    // Sprawdź dokumenty w folderze
    if (folder.documents && folder.documents.some(doc => doc.automationName)) {
      return true;
    }
    // Sprawdź dzieci rekurencyjnie
    if (folder.children && folder.children.length > 0) {
      for (const child of folder.children) {
        // Znajdź pełny obiekt folderu w folderTree (jeśli to root, folderTree to tablica rootów)
        const fullChild = folderTree.find(f => f.id === child.id);
        if (folderHasAutomatedTemplate(fullChild || child, folderTree)) return true;
      }
    }
    return false;
  }

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const day = date.toLocaleDateString('pl-PL');
    const time = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    const full = date.toLocaleString('pl-PL');
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    let label = day;
    if (isToday) label = 'Dziś';
    else if (isYesterday) label = 'Wczoraj';
    return { label, time, full };
  }

  if (authLoading) return <div>Ładowanie...</div>;
  if (!tenantId) return <div>Błąd: Brak tenantId</div>;

  let deleteFolderModal = null;
  if (isDeletingFolder) {
    const fullFolder = folderTree && folderTree.find(f => f.id === isDeletingFolder);
    const hasAutomated = fullFolder && folderHasAutomatedTemplate(fullFolder, folderTree);
    deleteFolderModal = (
      <div className="modal-overlay" onClick={() => setIsDeletingFolder(null)}>
        <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Usuń folder</h2>
            <button className="close-button" onClick={() => setIsDeletingFolder(null)}><X size={20} /></button>
          </div>
          <div style={{ margin: '18px 0', fontSize: 16 }}>
            {hasAutomated ? (
              <span style={{ color: '#ef4444', fontWeight: 600 }}>
                Nie można usunąć folderu, który zawiera zautomatyzowany szablon lub podfolder z takim szablonem.
              </span>
            ) : (
              'Czy na pewno chcesz usunąć ten folder? Wszystkie podfoldery i szablony w folderze zostaną usunięte.'
            )}
          </div>
          <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
            <Button className="outline-button" onClick={() => setIsDeletingFolder(null)}>Anuluj</Button>
            <Button className="upload-button" onClick={async () => { if (!hasAutomated) { await handleDeleteFolder(isDeletingFolder); setIsDeletingFolder(null); } }} disabled={hasAutomated}>Usuń</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="file-header enhanced-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', borderRadius: 18, boxShadow: '0 2px 16px rgba(37,99,235,0.07)', padding: '18px 32px 18px 24px', minHeight: 64, marginBottom: 5 }}>
        {/* BREADCRUMBS przeniesione na pasek nagłówka */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 0, marginTop: 0 }}>
          {isFullSearch && searchResults.length === 1 ? (
            breadcrumbs.map((b, i, arr) => (
              <span
                key={b.id || b.name || 'root'}
                style={{
                  color: i === arr.length - 1 ? '#2563eb' : '#888',
                  cursor: 'pointer',
                  fontWeight: i === arr.length - 1 ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 20,
                  background: dragOverBreadcrumbId === (b.id || 'root') ? '#e0e7ff' : 'transparent',
                  borderRadius: 8,
                  padding: '2px 6px',
                  transition: 'background 0.18s',
                }}
                onClick={() => i < arr.length - 1 && handleEnterFolderOrBreadcrumb(b.id)}
                onDragOver={e => { e.preventDefault(); setDragOverBreadcrumbId(b.id || 'root'); }}
                onDragLeave={e => { e.preventDefault(); setDragOverBreadcrumbId(null); }}
                onDrop={async e => {
                  e.preventDefault();
                  setDragOverBreadcrumbId(null);
                  if (draggedDocId && tenantId && i < arr.length - 1) {
                    await updateWordDocumentFolder(tenantId, draggedDocId, b.id || null);
                    setDraggedDocId(null);
                    await refreshTreeAndContext();
                  }
                }}
              >
                {i > 0 && <span style={{ color: '#bbb', fontWeight: 700, fontSize: 20, margin: '0 6px' }}>/</span>}
                {i < arr.length - 1 ? (
                  <Folder size={22} style={{ marginRight: 6, color: '#2563eb', filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                ) : (
                  <FileText size={22} style={{ marginRight: 6, color: '#2563eb' }} />
                )}
                {b.name}
              </span>
            ))
          ) : (
            breadcrumbs.map((b, i, arr) => (
              <span
                key={b.id || 'root'}
                style={{
                  color: i === arr.length - 1 ? '#2563eb' : '#888',
                  cursor: 'pointer',
                  fontWeight: i === arr.length - 1 ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 20,
                  background: dragOverBreadcrumbId === (b.id || 'root') ? '#e0e7ff' : 'transparent',
                  borderRadius: 8,
                  padding: '2px 6px',
                  transition: 'background 0.18s',
                }}
                onClick={() => handleEnterFolderOrBreadcrumb(b.id)}
                onDragOver={e => { e.preventDefault(); setDragOverBreadcrumbId(b.id || 'root'); }}
                onDragLeave={e => { e.preventDefault(); setDragOverBreadcrumbId(null); }}
                onDrop={async e => {
                  e.preventDefault();
                  setDragOverBreadcrumbId(null);
                  if (draggedDocId && tenantId && i < arr.length - 1) {
                    await updateWordDocumentFolder(tenantId, draggedDocId, b.id || null);
                    setDraggedDocId(null);
                    await refreshTreeAndContext();
                  }
                }}
              >
                {i > 0 && <span style={{ color: '#bbb', fontWeight: 700, fontSize: 20, margin: '0 6px' }}>/</span>}
                <Folder size={22} style={{ marginRight: 6, color: '#2563eb', filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} /> {b.name}
              </span>
            ))
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', marginRight: 10 }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setIsFullSearch(false);
                setSearchResults([]);
              }}
              onFocus={() => { if (searchQuery.trim()) setShowSuggestions(true); }}
              onKeyDown={handleSearchInputKeyDown}
              placeholder="Szukaj szablonu..."
              style={{
                padding: '10px 38px 10px 16px',
                borderRadius: 14,
                border: '1.5px solid #e0e7ff',
                fontSize: 16,
                minWidth: 340,
                background: '#fff',
                boxShadow: '0 2px 8px #e0e7ff33',
                outline: 'none',
                transition: 'border 0.18s, box-shadow 0.18s',
              }}
              autoFocus
              autoComplete="off"
            />
            <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#2563eb', pointerEvents: 'none' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            {showSuggestions && searchSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 44,
                zIndex: 1000,
                background: '#fff',
                border: '1.5px solid #e0e7ff',
                borderRadius: 12,
                boxShadow: '0 4px 24px #2563eb22',
                maxHeight: 320,
                overflowY: 'auto',
                animation: 'fadeIn 0.18s',
                minWidth: 600,
                maxWidth: 600,
              }}>
                {searchSuggestions.map((doc, idx) => (
                  <div
                    key={doc.id}
                    style={{
                      padding: '12px 18px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 16,
                      color: '#2563eb',
                      borderBottom: '1px solid #e0e7ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      background: idx === selectedSuggestionIndex ? '#e0e7ff' : '#fff',
                      transition: 'background 0.15s',
                    }}
                    onMouseDown={() => handleSuggestionClick(doc)}
                    onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                  >
                    <Download size={18} style={{ marginRight: 8 }} />
                    <span>{doc.fileName}</span>
                    <span style={{ color: '#888', fontWeight: 400, fontSize: 14, marginLeft: 10 }}>
                      {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('pl-PL') : ''}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    padding: '12px 18px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 15,
                    color: '#2563eb',
                    background: '#f3f6fd',
                    textAlign: 'center',
                    borderRadius: '0 0 12px 12px',
                  }}
                  onMouseDown={handleShowAllResults}
                >
                  Zobacz wszystkie wyniki
                </div>
              </div>
            )}
          </div>
          <Button onClick={() => window.open('/jak-przygotowac-szablon', '_blank')} className="info-btn enhanced-secondary-btn" style={{ padding: '12px 28px', fontSize: 16, borderRadius: 22, border: '2px solid #2563eb', background: '#fff', color: '#2563eb', fontWeight: 600, boxShadow: '0 2px 8px #e0e7ff', transition: 'box-shadow 0.18s' }}>
            <Info size={20} style={{ marginRight: 10 }} /> Instrukcje
          </Button>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Button onClick={() => setShowAddMenu(v => !v)} className="add-file-btn enhanced-primary-btn" style={{ fontWeight: 700, fontSize: 18, borderRadius: 26, padding: '12px 36px', background: 'linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)', color: '#fff', boxShadow: '0 4px 16px #2563eb33', letterSpacing: '0.5px', border: 'none', transition: 'box-shadow 0.18s, background 0.18s' }}>
              <Plus size={22} style={{ marginRight: 10 }} /> Dodaj
            </Button>
            {showAddMenu && (
              <div style={{ position: 'absolute', right: 0, top: 54, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(37,99,235,0.13)', minWidth: 240, zIndex: 1000, padding: '10px 0', animation: 'fadeIn 0.18s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 700, fontSize: 17, color: '#2563eb', borderRadius: 10 }} onClick={() => { setIsModalOpen(true); setShowAddMenu(false); }}>
                  <FileText size={22} style={{ marginRight: 10 }} /> Dodaj szablon
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 700, fontSize: 17, color: '#2563eb', borderRadius: 10 }} onClick={() => { setIsAddFolderOpen(true); setShowAddMenu(false); }}>
                  <FolderPlus size={22} style={{ marginRight: 10 }} /> Dodaj folder
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table-container enhanced-table-container" style={{ position: 'relative', background: '#fff', borderRadius: 18, boxShadow: '0 4px 32px rgba(37,99,235,0.07)', padding: '0 0 18px 0', marginBottom: 24, overflowX: 'auto', overflowY: 'auto' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <Loader />
          </div>
        )}
        <div style={{ height: 8 }} />
        <table className="file-table enhanced-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0 16px 18px', letterSpacing: '0.02em', color: '#222', textAlign: 'left', textTransform: 'none', minWidth: 220 }}>Nazwa</th>
              <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none', minWidth: 220 }}>Utworzono</th>
              <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none', minWidth: 220 }}>Typ</th>
              <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none', minWidth: 220 }}>Szczegóły</th>
              <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5}><Loader /></td></tr>
            ) : (
              <>
                {/* FOLDERY */}
                {!isFullSearch && folders.map(folder => {
                  const fullFolder = folderTree && folderTree.find(f => f.id === folder.id);
                  const hasAutomated = fullFolder && folderHasAutomatedTemplate(fullFolder, folderTree);
                  const automatedCount = fullFolder ? countAutomatedTemplates(fullFolder, folderTree) : 0;
                  return (
                    <tr
                      key={folder.id}
                      className={`enhanced-table-row${hoveredRowId === folder.id ? ' row-hover' : ''}`}
                      style={{ cursor: 'pointer', background: dragOverFolderId === folder.id ? '#e0e7ff' : (hoveredRowId === folder.id ? '#f0f6ff' : '#fff'), transition: 'background 0.18s', height: 28 }}
                      onMouseEnter={() => setHoveredRowId(folder.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onDoubleClick={() => handleEnterFolderOrBreadcrumb(folder.id)}
                      onDragOver={e => { e.preventDefault(); setDragOverFolderId(folder.id); }}
                      onDragLeave={e => { e.preventDefault(); setDragOverFolderId(null); }}
                      onDrop={async e => {
                        e.preventDefault();
                        setDragOverFolderId(null);
                        if (draggedDocId && tenantId) {
                          await updateWordDocumentFolder(tenantId, draggedDocId, folder.id);
                          setDraggedDocId(null);
                          await refreshTreeAndContext();
                        }
                      }}
                    >
                      <td style={{ fontWeight: 700, fontSize: 15, padding: '0 0 0 18px', minWidth: 220, lineHeight: 1, verticalAlign: 'middle' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <Folder size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                          <span style={{ color: '#18181b', fontWeight: 700 }}>{folder.name}</span>
                        </span>
                      </td>
                      <td style={{ color: '#18181b', fontWeight: 500, minWidth: 120, lineHeight: 1, fontSize: 14 }}
                        title={folder.createdAt ? formatDateTime(folder.createdAt).full : ''}
                      >
                        {folder.createdAt ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(folder.createdAt).label}</span>
                            <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                              <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                              {formatDateTime(folder.createdAt).time}
                            </span>
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ minWidth: 120, lineHeight: 1, fontSize: 14 }}>Folder</td>
                      <td style={{ minWidth: 180, lineHeight: 1, fontSize: 14 }}></td>
                      <td style={{ textAlign: 'right', minWidth: 80, lineHeight: 1, paddingRight: 10}}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 90, marginRight: 10}}>
                          <button className="icon-button edit enhanced-ghost-btn" title="Edytuj folder" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 2 }} onClick={e => { e.stopPropagation(); setIsEditingFolder(folder); setEditFolderName(folder.name); }}><MoreVertical size={15} /></button>
                          <button
                            className="icon-button delete enhanced-ghost-btn"
                            title={hasAutomated ? "Nie można usunąć folderu, który zawiera zautomatyzowany szablon" : "Usuń folder"}
                            style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', opacity: hasAutomated ? 0.5 : 1, cursor: hasAutomated ? 'not-allowed' : 'pointer' }}
                            onClick={e => { if (hasAutomated) { e.preventDefault(); e.stopPropagation(); setToast({ message: 'Nie można usunąć folderu, który zawiera zautomatyzowany szablon.', type: 'error' }); return; } e.stopPropagation(); setIsDeletingFolder(folder.id); }}
                            disabled={hasAutomated}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* PLIKI */}
                {isFullSearch ? (
                  searchResults.length === 0 ? (
                    <tr></tr>
                  ) : (
                    searchResults.map(doc => (
                      <tr key={doc.id} id={`doc-row-${doc.id}`}
                        style={{ background: hoveredRowId === doc.id ? '#f0f6ff' : '#fff', transition: 'background 0.18s', height: 28, cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredRowId(doc.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        draggable={true}
                        onDragStart={() => setDraggedDocId(doc.id)}
                        onDragEnd={() => setDraggedDocId(null)}
                      >
                        <td style={{ fontWeight: 700, fontSize: 15, padding: '0 0 0 18px', minWidth: 220, lineHeight: 1, verticalAlign: 'middle' }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <FileText size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                            <span
                              style={{ color: '#18181b', fontWeight: 700, cursor: 'pointer' }}
                              title="Kliknij dwukrotnie, aby podejrzeć dokument"
                              onDoubleClick={e => { e.stopPropagation(); setPreviewDoc(doc); }}
                            >
                              {doc.fileName}
                            </span>
                          </span>
                        </td>
                        <td style={{ color: '#18181b', fontWeight: 500, minWidth: 120, lineHeight: 1, fontSize: 14 }}
                          title={doc.createdAt ? formatDateTime(doc.createdAt).full : ''}
                        >
                          {doc.createdAt ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(doc.createdAt).label}</span>
                              <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                                <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                                {formatDateTime(doc.createdAt).time}
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ minWidth: 120, lineHeight: 1, fontSize: 14 }}>Szablon</td>
                        <td style={{ minWidth: 180, lineHeight: 1, fontSize: 14 }}>
                          {doc.automationName ? (
                            <span
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#e0e7ff', borderRadius: 20, padding: '7px 22px', boxShadow: '0 2px 8px #c7d2fe', fontWeight: 700, fontSize: 15, color: '#2563eb', minWidth: 220, cursor: 'pointer' }}
                              title="Kliknij, aby zobaczyć szczegóły szablonu"
                              onClick={e => { e.stopPropagation(); setSelectedDoc(doc); setIsDetailsOpen(true); }}
                            >
                              <CheckCircle2 size={18} style={{ color: '#2563eb' }} /> Zautomatyzowany
                            </span>
                          ) : (
                            <span
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f3f6fd', borderRadius: 20, padding: '7px 22px', boxShadow: '0 2px 8px #e0e7ff', fontWeight: 700, fontSize: 15, color: '#64748b', minWidth: 220, cursor: 'pointer' }}
                              title="Kliknij, aby zobaczyć szczegóły szablonu"
                              onClick={e => { e.stopPropagation(); setSelectedDoc(doc); setIsDetailsOpen(true); }}
                            >
                              <MinusCircle size={18} style={{ color: '#64748b' }} /> Brak automatyzacji
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', minWidth: 80, lineHeight: 1, paddingRight: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 90, marginRight: 10 }}>
                            <button
                              className="icon-button download enhanced-ghost-btn"
                              title="Pobierz szablon"
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 2 }}
                              onClick={e => { e.stopPropagation(); handleDownload(doc.id); }}
                            >
                              <Download size={15} />
                            </button>
                            <button
                              className="icon-button edit enhanced-ghost-btn"
                              title="Edytuj nazwę szablonu"
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 2 }}
                              onClick={e => { e.stopPropagation(); setIsEditingDoc(doc); setEditDocName(doc.fileName); }}
                            >
                              <MoreVertical size={15} />
                            </button>
                            <button
                              className="icon-button delete enhanced-ghost-btn"
                              title={doc.automationName ? "Nie można usunąć szablonu powiązanego z automatyzacją" : "Usuń szablon"}
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', opacity: doc.automationName ? 0.5 : 1, cursor: doc.automationName ? 'not-allowed' : 'pointer' }}
                              onClick={e => { if (doc.automationName) { e.preventDefault(); e.stopPropagation(); setToast({ message: 'Nie można usunąć szablonu powiązanego z automatyzacją.', type: 'error' }); return; } e.stopPropagation(); setConfirmDeleteId(doc.id); setConfirmDeleteName(doc.fileName); }}
                              disabled={!!doc.automationName}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                ) : (
                  documents.length === 0 && folders.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 16, padding: 10 }}>Brak plików ani folderów.</td></tr>
                  ) : (
                    documents.map((doc) => (
                      <tr
                        key={doc.id}
                        style={{ background: hoveredRowId === doc.id ? '#f0f6ff' : '#fff', transition: 'background 0.18s', height: 28, cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredRowId(doc.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        draggable={true}
                        onDragStart={() => setDraggedDocId(doc.id)}
                        onDragEnd={() => setDraggedDocId(null)}
                      >
                        <td style={{ fontWeight: 700, fontSize: 15, padding: '0 0 0 18px', minWidth: 220, lineHeight: 1, verticalAlign: 'middle' }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <FileText size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                            <span
                              style={{ color: '#18181b', fontWeight: 700, cursor: 'pointer' }}
                              title="Kliknij dwukrotnie, aby podejrzeć dokument"
                              onDoubleClick={e => { e.stopPropagation(); setPreviewDoc(doc); }}
                            >
                              {doc.fileName}
                            </span>
                          </span>
                        </td>
                        <td style={{ color: '#18181b', fontWeight: 500, minWidth: 120, lineHeight: 1, fontSize: 14 }}
                          title={doc.createdAt ? formatDateTime(doc.createdAt).full : ''}
                        >
                          {doc.createdAt ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(doc.createdAt).label}</span>
                              <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                                <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                                {formatDateTime(doc.createdAt).time}
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ minWidth: 120, lineHeight: 1, fontSize: 14 }}>Szablon</td>
                        <td style={{ minWidth: 180, lineHeight: 1, fontSize: 14 }}>
                          {doc.automationName ? (
                            <span
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#e0e7ff', borderRadius: 20, padding: '7px 22px', boxShadow: '0 2px 8px #c7d2fe', fontWeight: 700, fontSize: 15, color: '#2563eb', minWidth: 220, cursor: 'pointer' }}
                              title="Kliknij, aby zobaczyć szczegóły szablonu"
                              onClick={e => { e.stopPropagation(); setSelectedDoc(doc); setIsDetailsOpen(true); }}
                            >
                              <CheckCircle2 size={18} style={{ color: '#2563eb' }} /> Zautomatyzowany
                            </span>
                          ) : (
                            <span
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f3f6fd', borderRadius: 20, padding: '7px 22px', boxShadow: '0 2px 8px #e0e7ff', fontWeight: 700, fontSize: 15, color: '#64748b', minWidth: 220, cursor: 'pointer' }}
                              title="Kliknij, aby zobaczyć szczegóły szablonu"
                              onClick={e => { e.stopPropagation(); setSelectedDoc(doc); setIsDetailsOpen(true); }}
                            >
                              <MinusCircle size={18} style={{ color: '#64748b' }} /> Brak automatyzacji
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', minWidth: 80, lineHeight: 1, paddingRight: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 90, marginRight: 10 }}>
                            <button
                              className="icon-button download enhanced-ghost-btn"
                              title="Pobierz szablon"
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 2 }}
                              onClick={e => { e.stopPropagation(); handleDownload(doc.id); }}
                            >
                              <Download size={15} />
                            </button>
                            <button
                              className="icon-button edit enhanced-ghost-btn"
                              title="Edytuj nazwę szablonu"
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 2 }}
                              onClick={e => { e.stopPropagation(); setIsEditingDoc(doc); setEditDocName(doc.fileName); }}
                            >
                              <MoreVertical size={15} />
                            </button>
                            <button
                              className="icon-button delete enhanced-ghost-btn"
                              title={doc.automationName ? "Nie można usunąć szablonu powiązanego z automatyzacją" : "Usuń szablon"}
                              style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', opacity: doc.automationName ? 0.5 : 1, cursor: doc.automationName ? 'not-allowed' : 'pointer' }}
                              onClick={e => { if (doc.automationName) { e.preventDefault(); e.stopPropagation(); setToast({ message: 'Nie można usunąć szablonu powiązanego z automatyzacją.', type: 'error' }); return; } e.stopPropagation(); setConfirmDeleteId(doc.id); setConfirmDeleteName(doc.fileName); }}
                              disabled={!!doc.automationName}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <UploadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onUploadSuccess={() => { refreshTreeAndContext(); }} currentFolderId={currentFolderId} />
      <ConfirmModal
        isOpen={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (confirmDeleteId) {
            await handleDelete(confirmDeleteId);
            setConfirmDeleteId(null);
          }
        }}
        fileName={confirmDeleteName}
      />
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {isDetailsOpen && selectedDoc && (
        <div className="modal-overlay" onClick={() => setIsDetailsOpen(false)}>
          <div className="modal-content upload-modal" style={{minWidth: 420, maxWidth: 520, padding: '40px 32px 32px 32px', borderRadius: 20}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Szczegóły szablonu</h2>
              <button className="close-button" style={{ marginLeft: 12 }} onClick={() => setIsDetailsOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div style={{margin: '18px 0 18px 0', display: 'flex', gap: 24, alignItems: 'flex-start'}}>
              <div style={{width: 64, height: 64, borderRadius: 12, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #2563eb', marginRight: 50, marginTop: 10}}>
                <WordIcon style={{ width: 60, height: 60, marginRight: 8 , marginBottom: -2}} />
              </div>
              <div style={{flex: 1}}>
                <div style={{fontWeight: 600, fontSize: 17, marginBottom: 8}}>{selectedDoc.fileName}</div>
                <div style={{color: '#64748b', fontSize: 15, marginBottom: 8}}>Dodano: {new Date(selectedDoc.createdAt).toLocaleDateString("pl-PL")}</div>
                <div style={{marginBottom: 16}}>
                  {selectedDoc.automationName ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dbeafe', color: '#2563eb', borderRadius: 16, padding: '4px 12px', fontWeight: 600, fontSize: 15 }}>
                      <CheckCircle2 size={18} /> Zautomatyzowany: {selectedDoc.automationName}
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', color: '#64748b', borderRadius: 16, padding: '4px 12px', fontWeight: 500, fontSize: 15 }}>
                      <MinusCircle size={18} /> Brak automatyzacji
                    </span>
                  )}
                </div>
                {selectedDoc.automationName && (
                  <div style={{fontWeight: 500, fontSize: 16, marginBottom: 6}}>
                    <a href="#" style={{color: '#2563eb', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer'}}
                      onClick={e => {
                        e.preventDefault();
                        setIsDetailsOpen(false);
                        setActiveTab('automations');
                        if (onJumpToAutomation) onJumpToAutomation(
                          selectedDoc.automationName,
                          selectedDoc.folderId || null,
                          selectedDoc.automationId
                        );
                      }}
                    >
                      Przejdź do automatyzacji
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{gap: 16, marginTop: 0, justifyContent: 'flex-end'}}>
              <Button className="outline-button" style={{padding: '10px 24px', fontSize: 16}} onClick={() => setIsDetailsOpen(false)}>Zamknij</Button>
            </div>
          </div>
        </div>
      )}
      {isAddFolderOpen && (
        <div className="modal-overlay" onClick={() => setIsAddFolderOpen(false)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Dodaj folder</h2>
              <button className="close-button" onClick={() => setIsAddFolderOpen(false)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa folderu" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} style={{ margin: '18px 0' }} onKeyDown={e => { if (e.key === 'Enter' && newFolderName) { handleAddFolder(newFolderName); setNewFolderName(""); setIsAddFolderOpen(false); } }} autoFocus />
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsAddFolderOpen(false)}>Anuluj</Button>
              <Button className="upload-button" disabled={!newFolderName} onClick={async () => {
                try {
                  await handleAddFolder(newFolderName);
                  setNewFolderName("");
                  setIsAddFolderOpen(false);
                } catch {}
              }}>Dodaj</Button>
            </div>
          </div>
        </div>
      )}
      {isEditingFolder && (
        <div className="modal-overlay" onClick={() => setIsEditingFolder(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Zmień nazwę folderu</h2>
              <button className="close-button" onClick={() => setIsEditingFolder(null)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa folderu" value={editFolderName} onChange={e => setEditFolderName(e.target.value)} style={{ margin: '18px 0' }} />
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsEditingFolder(null)}>Anuluj</Button>
              <Button className="upload-button" disabled={!editFolderName} onClick={async () => {
                if (!tenantId || !isEditingFolder?.id || !editFolderName) return;
                try {
                  await updateTemplateFolderName(tenantId, isEditingFolder.id, editFolderName);
                  setToast({ message: 'Nazwa folderu została zmieniona!', type: 'success' });
                  await refreshTreeAndContext();
                  setIsEditingFolder(null);
                  setEditFolderName("");
                } catch (e) {
                  setToast({ message: 'Błąd zmiany nazwy folderu.', type: 'error' });
                }
              }}>Zapisz</Button>
            </div>
          </div>
        </div>
      )}
      {isEditingDoc && (
        <div className="modal-overlay" onClick={() => setIsEditingDoc(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Zmień nazwę szablonu</h2>
              <button className="close-button" onClick={() => setIsEditingDoc(null)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa szablonu" value={editDocName} onChange={e => setEditDocName(e.target.value)} style={{ margin: '18px 0' }} />
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsEditingDoc(null)}>Anuluj</Button>
              <Button className="upload-button" disabled={!editDocName} onClick={async () => {
                if (!tenantId || !isEditingDoc?.id || !editDocName) return;
                try {
                  await updateWordDocumentName(tenantId, isEditingDoc.id, editDocName);
                  setToast({ message: 'Nazwa szablonu została zmieniona!', type: 'success' });
                  await refreshTreeAndContext();
                  if (isFullSearch) {
                    setSearchQuery("");
                    setIsFullSearch(false);
                    setSearchResults([]);
                    if (isEditingDoc.folderId) {
                      setCurrentFolderId(isEditingDoc.folderId);
                    }
                  }
                  setIsEditingDoc(null);
                  setEditDocName("");
                } catch (e) {
                  setToast({ message: 'Błąd zmiany nazwy szablonu.', type: 'error' });
                }
              }}>Zapisz</Button>
            </div>
          </div>
        </div>
      )}
      {deleteFolderModal}
      {isAgendaOpen && (
        <div className="modal-overlay" onClick={() => setIsAgendaOpen(false)}>
          <div className="modal-content upload-modal" style={{maxWidth: 480, minWidth: 0, padding: '40px 32px 32px 32px'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{fontSize: '1.35rem', fontWeight: 700, margin: 0}}>Jak przygotować szablon?</h2>
              <button className="close-button" style={{marginLeft: 12}} onClick={() => setIsAgendaOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div style={{margin: '0 0 24px 0', color: '#1a1a1a', fontSize: 17, fontWeight: 500, textAlign: 'left', width: '100%', lineHeight: 1.6, letterSpacing: '-0.2px'}}>
              <ul style={{paddingLeft: 22, margin: 0}}>
                <li>Szablon musi być w formacie <b>.docx</b> lub <b>.doc</b>.</li>
                <li>Unikaj zabezpieczeń w pliku.</li>
                <li>Oznacz miejsca na dane do automatyzacji <br/> np. {'<< IMIE I NAZWISKO >>'}</li>
                <li>Możesz też dodać inne pola np. {'<< EMAIL >>'}</li>
                <li>Pola w znaczkach {'<< >>'} będą pokazane w formularzu automatyzacji i wypełniane przez system.</li>
                <li>Przykład poprawnego szablonu znajdziesz w dokumentacji lub u administratora.</li>
                <li style={{marginTop: 12, color: '#2563eb'}}>
                  <b>Nowość!</b> Sprawdź <a href="/jak-przygotowac-szablon" target="_blank" rel="noopener noreferrer" style={{color: '#2563eb', textDecoration: 'underline'}}>Kreator Formuł Logicznych</a> – narzędzie do budowania warunków IF/ELSE do Worda.
                </li>
              </ul>
            </div>
            <div className="modal-footer" style={{gap: 16, marginTop: 0, justifyContent: 'flex-end'}}>
              <Button className="upload-button" style={{padding: '10px 24px', fontSize: 16}} onClick={() => setIsAgendaOpen(false)}>Zamknij</Button>
            </div>
          </div>
        </div>
      )}
      {previewDoc && (
        <WordPreview
          tenantId={tenantId}
          documentId={previewDoc.id}
          title={previewDoc.fileName}
          onClose={() => setPreviewDoc(null)}
          previewType="template"
        />
      )}
      <style>{`
        .toast {
          position: fixed;
          right: 32px;
          bottom: 32px;
          min-width: 240px;
          max-width: 320px;
          background: #fff;
          color: #222;
          border-radius: 10px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.13);
          padding: 16px 22px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1rem;
          font-weight: 500;
          z-index: 999999;
          animation: toastIn 0.3s cubic-bezier(.4,2,.6,1);
        }
        .toast.success { border-left: 5px solid #22c55e; }
        .toast.error { border-left: 5px solid #ef4444; }
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pagination-circle, .pagination-arrow {
          width: 36px;
          height: 36px;
          border-radius: 50% !important;
          border: 2px solid #ccc;
          background: #fff;
          margin: 0 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: border 0.18s, color 0.18s, background 0.18s;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0;
          box-sizing: border-box;
        }
        .pagination-circle.active {
          border: 2.5px solid #2563eb;
          color: #2563eb !important;
          background: #fff;
        }
        .pagination-circle:hover, .pagination-arrow:hover:not(:disabled) {
          border: 2px solid #2563eb;
          color: #2563eb;
          background: #f0f6ff;
        }
        .pagination-arrow:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          border: 2px solid #eee;
          color: #bbb;
          background: #fff;
        }
        .enhanced-header {
          margin-bottom: 5px !important;
        }
        .enhanced-primary-btn:hover, .enhanced-primary-btn:focus {
          background: linear-gradient(90deg, #1746a0 0%, #2563eb 100%) !important;
          box-shadow: 0 6px 24px #2563eb33 !important;
          outline: 2px solid #2563eb44;
        }
        .enhanced-secondary-btn:hover, .enhanced-secondary-btn:focus {
          background: #f3f6fd !important;
          box-shadow: 0 4px 16px #c7d2fe !important;
          outline: 2px solid #2563eb22;
        }
        .enhanced-table-container {
          margin-top: 0 !important;
          padding: 0 0 18px 0 !important;
          box-shadow: 0 4px 32px rgba(37,99,235,0.07) !important;
          border-radius: 18px !important;
          background: #fff !important;
        }
        .enhanced-table thead tr {
          background: #f3f6fd !important;
          border-bottom: 2.5px solid #e0e7ff !important;
          height: 56px !important;
        }
        .enhanced-table th {
          font-weight: 800 !important;
          font-size: 17px !important;
          padding: 16px 0 16px 18px !important;
          color: #222 !important;
          background: #f3f6fd !important;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .enhanced-table td {
          font-size: 15px !important;
          font-weight: 600 !important;
          padding: 10px 0 10px 18px !important;
          background: #fff !important;
          border-bottom: 1.5px solid #f1f5f9 !important;
          transition: background 0.18s, box-shadow 0.18s;
        }
        .enhanced-table tr:hover {
          background: #f0f6ff !important;
          box-shadow: 0 2px 12px #c7d2fe44 !important;
        }
        .enhanced-table-row:hover {
          background: #f0f6ff !important;
          box-shadow: 0 2px 12px #c7d2fe44 !important;
        }
        .icon-button.enhanced-ghost-btn:hover, .icon-button.enhanced-ghost-btn:focus, .icon-btn.enhanced-ghost-btn:hover, .icon-btn.enhanced-ghost-btn:focus {
          background: #e0e7ff !important;
          box-shadow: 0 4px 16px #c7d2fe !important;
          outline: 2px solid #2563eb22;
          transition: background 0.18s, box-shadow 0.18s;
        }
      `}</style>
    </>
  );
}
